import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Bitcoin, TrendingUp, Cpu, BookOpen } from 'lucide-react';

const categoryIcon: Record<string, React.ReactNode> = {
  Bitcoin: <Bitcoin   size={14} />,
  Macro:   <TrendingUp size={14} />,
  AI:      <Cpu       size={14} />,
};

const categoryColor: Record<string, string> = {
  Bitcoin: '#F7931A',
  Macro:   '#3B82F6',
  AI:      '#10B981',
};

interface Article {
  id: string;
  date: string;
  category: string;
  categoryColor: string;
  title: string;
  excerpt: string;
  image?: string;
}

const meshColors: Record<string, { orb1: string; orb2: string; orb3: string }> = {
  Bitcoin: { orb1: 'rgba(247,147,26,0.22)', orb2: 'rgba(180,80,0,0.14)',   orb3: 'rgba(247,147,26,0.10)' },
  Macro:   { orb1: 'rgba(59,130,246,0.20)', orb2: 'rgba(30,58,138,0.15)',   orb3: 'rgba(99,102,241,0.10)' },
  AI:      { orb1: 'rgba(16,185,129,0.18)', orb2: 'rgba(5,150,105,0.14)',   orb3: 'rgba(52,211,153,0.10)' },
};

const fallbackMesh = { orb1: 'rgba(247,147,26,0.15)', orb2: 'rgba(180,80,0,0.10)', orb3: 'rgba(247,147,26,0.08)' };

interface HeroCarouselProps {
  onNavigate: (page: string, articleId?: string) => void;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ onNavigate }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [current, setCurrent]   = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Fetch top 3 most recent articles
  useEffect(() => {
    fetch('/data/articles.json')
      .then(r => r.ok ? r.json() : [])
      .then((data: Article[]) => {
        const sorted = [...data].sort((a, b) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setArticles(sorted.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  const count = articles.length;
  const next = useCallback(() => setCurrent(c => (c + 1) % Math.max(count, 1)), [count]);
  const prev = useCallback(() => setCurrent(c => (c - 1 + Math.max(count, 1)) % Math.max(count, 1)), [count]);

  // Auto-advance
  useEffect(() => {
    if (isHovered || count < 2) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, isHovered, count]);

  // Animated mesh orbs
  const [orbPos, setOrbPos] = useState({ x1: 20, y1: 30, x2: 70, y2: 60, x3: 50, y3: 20 });
  useEffect(() => {
    let frame: number;
    let t = 0;
    const animate = () => {
      t += 0.003;
      setOrbPos({
        x1: 18 + Math.sin(t * 0.7) * 14,
        y1: 28 + Math.cos(t * 0.5) * 12,
        x2: 68 + Math.cos(t * 0.6) * 16,
        y2: 58 + Math.sin(t * 0.8) * 14,
        x3: 48 + Math.sin(t * 0.9 + 1) * 12,
        y3: 22 + Math.cos(t * 0.4 + 2) * 10,
      });
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  // Loading / empty state
  if (count === 0) {
    return (
      <div className="relative w-full flex items-center justify-center" style={{ height: '500px', background: '#0A0A0A' }}>
        <BookOpen size={32} className="text-gray-700 animate-pulse" />
      </div>
    );
  }

  const slide = articles[current];
  const color = slide.categoryColor || categoryColor[slide.category] || '#F7931A';
  const mc    = meshColors[slide.category] || fallbackMesh;

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ minHeight: '500px', height: 'auto', background: '#0A0A0A', transition: 'background 0.6s ease' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated gradient mesh orbs */}
      <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 1 }}>
        {[
          { left: orbPos.x1, top: orbPos.y1, size: 520, bg: mc.orb1, blur: true },
          { left: orbPos.x2, top: orbPos.y2, size: 400, bg: mc.orb2, blur: true },
          { left: orbPos.x3, top: orbPos.y3, size: 300, bg: mc.orb3, blur: false },
        ].map((orb, i) => (
          <div key={i} style={{
            position: 'absolute',
            left: `${orb.left}%`, top: `${orb.top}%`,
            width: `${orb.size}px`, height: `${orb.size}px`,
            background: `radial-gradient(circle, ${orb.bg} 0%, transparent 70%)`,
            transform: 'translate(-50%, -50%)',
            transition: 'background 0.8s ease',
            filter: orb.blur ? 'blur(2px)' : undefined,
          }} />
        ))}
      </div>

      {/* Bottom fade overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]/90" style={{ zIndex: 2 }} />

      {/* Right-side image with alpha mask */}
      {slide.image && (
        <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 1 }}>
          <div
            className="absolute top-0 right-0 h-full"
            style={{
              width: '62%',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.2) 18%, rgba(0,0,0,0.7) 38%, rgba(0,0,0,0.9) 55%, black 70%), linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)',
              WebkitMaskComposite: 'source-in',
              maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.2) 18%, rgba(0,0,0,0.7) 38%, rgba(0,0,0,0.9) 55%, black 70%), linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)',
              maskComposite: 'intersect',
            }}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover cursor-pointer"
              style={{ opacity: 0.5, filter: 'saturate(0.75) brightness(0.85)' }}
              onClick={() => onNavigate('articles', slide.id)}
              role="button"
              aria-label={`Open article: ${slide.title}`}
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-start sm:justify-center pt-10 pb-16 sm:pt-0 sm:pb-0" style={{ zIndex: 3, minHeight: '500px' }}>
        {/* Category badge */}
        <div className="mb-4">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-xl border"
            style={{ color, borderColor: `${color}50`, backgroundColor: `${color}15` }}
          >
            {categoryIcon[slide.category] ?? <BookOpen size={14} />}
          </span>
        </div>

        {/* Headline */}
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 text-gradient"
          style={{ maxWidth: slide.image ? 'min(58%, 100%)' : '75%', width: '100%' }}
        >
          {slide.title}
        </h1>

        {/* Excerpt */}
        <p
          className="text-gray-300 text-base sm:text-lg mb-8 leading-relaxed"
          style={{ maxWidth: slide.image ? 'min(50%, 100%)' : '65%', width: '100%' }}
        >
          {slide.excerpt}
        </p>

        {/* CTA */}
        <div>
          <button
            onClick={() => onNavigate('articles', slide.id)}
            className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 rounded-lg transition-all hover:opacity-90"
            style={{ backgroundColor: color, color: '#000' }}
          >
            Read Article →
          </button>
        </div>
      </div>

      {/* Date badge */}
      <div className="absolute bottom-16 right-6 text-gray-500 text-xs" style={{ zIndex: 3 }}>
        {slide.date}
      </div>

      {/* Arrows — show on hover */}
      {count > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 hover:bg-black/70 text-white transition-all border border-white/10"
            style={{ zIndex: 4, opacity: isHovered ? 1 : 0, transition: 'opacity 0.3s' }}
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 hover:bg-black/70 text-white transition-all border border-white/10"
            style={{ zIndex: 4, opacity: isHovered ? 1 : 0, transition: 'opacity 0.3s' }}
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {count > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2" style={{ zIndex: 4 }}>
          {articles.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className="rounded-full transition-all"
              style={{
                width: i === current ? '24px' : '8px',
                height: '8px',
                backgroundColor: i === current ? '#F7931A' : 'rgba(255,255,255,0.3)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroCarousel;
